import type { InternalNote } from "@nlc/shared-types";

import dayjs from "dayjs";
import {
  EllipsisVerticalIcon,
  LinkIcon,
  PencilIcon,
  Trash2Icon,
} from "lucide-react";
import { Button as RACButton } from "react-aria-components";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Menu, MenuItem, MenuItemLabel, MenuTrigger } from "@/components/menu";
import { Popover } from "@/components/popover";
import { TiptapEditor } from "@/components/ui/TiptapEditor";

type NoteCardProps = {
  canEdit: boolean;
  isDeleting: boolean;
  note: InternalNote;
  onCopyLink: (note: InternalNote) => void;
  onDelete: (note: InternalNote) => void;
  onEdit: (note: InternalNote) => void;
};

export function NoteCard({
  canEdit,
  isDeleting,
  note,
  onCopyLink,
  onDelete,
  onEdit,
}: NoteCardProps) {
  const isEdited =
    note.updatedAt && note.createdAt && note.updatedAt !== note.createdAt;

  return (
    <div
      className="flex flex-col gap-sm py-xl first:mt-0 last:mb-0 border border-border-primary rounded-2xl p-5 mb-2"
      id={`note-${note.id}`}
    >
      <div className="flex items-start justify-between gap-md">
        <div className="flex min-w-0 flex-col gap-xs">
          <p className="truncate font-bold text-xl text-text-primary">
            {note.name}
          </p>
          {isEdited ? (
            <p className="text-sm leading-tight text-text-tertiary">
              Last edited{" "}
              {note.createdAt ? dayjs(note.createdAt).format("MMM D") : ""}
              {note.creator ? (
                <span>
                  , by <span className="font-medium">{note.creator.name}</span>
                </span>
              ) : null}
            </p>
          ) : (
            <p className="text-sm leading-tight text-text-tertiary">
              {note.createdAt ? dayjs(note.createdAt).format("MMM D") : ""}
              {note.creator ? (
                <span>
                  , by <span className="font-medium">{note.creator.name}</span>
                </span>
              ) : (
                <span>
                  , by <span className="font-medium">Migration services</span>
                </span>
              )}
            </p>
          )}
        </div>

        <MenuTrigger>
          <RACButton
            aria-label="Note actions"
            className="shrink-0 rounded-sm p-xs text-text-tertiary outline-hidden hover:bg-bg-active border border-border-tertiary hover:border-border-primary cursor-pointer"
          >
            <EllipsisVerticalIcon size={18} />
          </RACButton>
          <Popover>
            <Menu className="w-[192px]">
              <MenuItem onAction={() => onCopyLink(note)}>
                <AccessibleIcon>
                  <LinkIcon />
                </AccessibleIcon>
                <MenuItemLabel>Copy note link</MenuItemLabel>
              </MenuItem>
              {canEdit && (
                <MenuItem onAction={() => onEdit(note)}>
                  <AccessibleIcon>
                    <PencilIcon />
                  </AccessibleIcon>
                  <MenuItemLabel>Edit note</MenuItemLabel>
                </MenuItem>
              )}
              {canEdit && (
                <MenuItem
                  destructive
                  isDisabled={isDeleting}
                  onAction={() => onDelete(note)}
                >
                  <AccessibleIcon>
                    <Trash2Icon />
                  </AccessibleIcon>
                  <MenuItemLabel>Delete</MenuItemLabel>
                </MenuItem>
              )}
            </Menu>
          </Popover>
        </MenuTrigger>
      </div>

      {note.description && (
        <p className="mt-3 text-text-secondary">
          <TiptapEditor
            borderless
            isReadOnly
            key={note.updatedAt ?? note.createdAt}
            value={note.description}
          />
        </p>
      )}
    </div>
  );
}
